import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border py-0 font-medium whitespace-nowrap transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 [&>svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "border-[hsl(var(--badge-primary-tone)_/_0.2)] bg-[hsl(var(--badge-primary-tone)_/_0.08)] text-[hsl(var(--badge-primary-foreground))]",
        outline:
          "border-[hsl(var(--badge-outline-border))] bg-[hsl(var(--badge-outline-bg))] text-[hsl(var(--badge-outline-foreground))]",
        secondary:
          "border-transparent bg-[hsl(var(--badge-secondary-bg))] text-[hsl(var(--badge-secondary-foreground))]",
        success:
          "border-[hsl(var(--badge-green-tone)_/_0.2)] bg-[hsl(var(--badge-green-tone)_/_0.08)] text-[hsl(var(--badge-green-foreground))]",
        warning:
          "border-[hsl(var(--badge-amber-tone)_/_0.2)] bg-[hsl(var(--badge-amber-tone)_/_0.08)] text-[hsl(var(--badge-amber-foreground))]",
        danger:
          "border-[hsl(var(--badge-red-tone)_/_0.2)] bg-[hsl(var(--badge-red-tone)_/_0.08)] text-[hsl(var(--badge-red-foreground))]",
        info:
          "border-[hsl(var(--badge-sky-tone)_/_0.2)] bg-[hsl(var(--badge-sky-tone)_/_0.08)] text-[hsl(var(--badge-sky-foreground))]",
        model:
          "border-[hsl(var(--badge-violet-tone)_/_0.2)] bg-[hsl(var(--badge-violet-tone)_/_0.08)] text-[hsl(var(--badge-violet-foreground))] normal-case",
        worker:
          "border-[hsl(var(--badge-amber-tone)_/_0.2)] bg-[hsl(var(--badge-amber-tone)_/_0.08)] text-[hsl(var(--badge-amber-foreground))] normal-case",
        live:
          "border-[hsl(var(--badge-teal-tone)_/_0.2)] bg-[hsl(var(--badge-teal-tone)_/_0.08)] text-[hsl(var(--badge-teal-foreground))] normal-case",
        queued:
          "border-[hsl(var(--badge-orange-tone)_/_0.2)] bg-[hsl(var(--badge-orange-tone)_/_0.08)] text-[hsl(var(--badge-orange-foreground))]",
        completed:
          "border-[hsl(var(--badge-green-tone)_/_0.2)] bg-[hsl(var(--badge-green-tone)_/_0.08)] text-[hsl(var(--badge-green-foreground))]",
        plugin:
          "border-[hsl(var(--badge-sky-tone)_/_0.2)] bg-[hsl(var(--badge-sky-tone)_/_0.08)] text-[hsl(var(--badge-sky-foreground))] normal-case",
        skill:
          "border-[hsl(var(--badge-violet-tone)_/_0.2)] bg-[hsl(var(--badge-violet-tone)_/_0.08)] text-[hsl(var(--badge-violet-foreground))] normal-case",
        tool:
          "border-[hsl(var(--badge-green-tone)_/_0.2)] bg-[hsl(var(--badge-green-tone)_/_0.08)] text-[hsl(var(--badge-green-foreground))] normal-case",
        event:
          "border-border bg-transparent text-muted-foreground",
        datetime:
          "border-transparent bg-transparent text-muted-foreground italic normal-case tabular-nums",
      },
      size: {
        default: "h-5 px-1.5 text-sm has-[svg]:px-1.5 [&>svg]:size-3",
        xs: "h-4 gap-0.5 px-1 text-[10px] has-[svg]:px-1 [&>svg]:size-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Badge({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
